// @ts-nocheck
import { AIAnalysisAdapter, debriefLabels } from "../adapters/AIAnalysisAdapter.js";
import { RemoteStorageAdapter } from "../adapters/RemoteStorageAdapter.js";
import { getApiEndpoint, loadSignedFetch } from "../adapters/SignedApi.js";
import { CONFIG } from "../constants.js";
import { prefersReducedMotion } from "../utils/motion.js";
import { typewrite } from "../ui/Typewriter.js";
import { LeaderboardManager } from "./LeaderboardManager.js";
import { FocusManager } from "./FocusManager.js";

/** Typing pace for the headline when there is no voice to follow, and its bounds. */
const TYPE_MS_PER_CHAR = 32;
const TYPE_MIN_MS = 1000;
const TYPE_MAX_MS = 3500;
/** Longest wait for the narration clip's timing before typing without it. */
const SPEECH_TIMING_WAIT_MS = 1500;
/**
 * Longest wait for the remote debrief before the offline one: a cold Lambda plus Bedrock and the
 * voice (Polly or ElevenLabs) can pass the default 8 s, and the incoming transmission covers the
 * wait.
 */
const ANALYSIS_TIMEOUT_MS = 15000;
/** Signal bars while the transmission is incoming, and how long they show "locked" on arrival. */
const SIGNAL_BARS = 6;
const SIGNAL_LOCK_MS = 350;
/** Voice meter bars on each side of the commander's name, their relative heights, rest level. */
const METER_SHAPE = [0.55, 0.9, 1.25];
const METER_REST = 0.15;
/** How long the meter keeps reading after the voice should have ended. */
const METER_TAIL_MS = 400;

/**
 * Element with `count` empty `<i>` bars.
 * @param {string} className
 * @param {number} count
 * @returns {HTMLSpanElement}
 */
function barsElement(className, count) {
  const el = document.createElement("span");
  el.className = className;
  el.setAttribute("aria-hidden", "true");
  for (let i = 0; i < count; i++) el.appendChild(document.createElement("i"));
  return el;
}

/**
 * The commander who debriefs (see managers/Commanders.js); Commander Vega without history.
 * @param {{ commander?: string }|null|undefined} history
 * @returns {"vega"|"altair"}
 */
const commanderOf = (history) => (history && history.commander === "altair" ? "altair" : "vega");

/**
 * AIAnalysisManager – static facade to compute and render post-game AI analysis.
 * Mirrors the online/offline gating style of LeaderboardManager.
 *
 * Presentation: while a remote debrief loads, the screen shows an "incoming transmission" from
 * the mission's commander, Commander Vega or Captain Altair (localized, each with an accent colour
 * via `commander-<id>` on the container), with a radio crackle and signal bars that light up one
 * by one, the last searching until the debrief arrives and the signal shows "locked" for a
 * moment; the outcome title and the Ok button are hidden until the debrief arrives (then shown,
 * Ok enabled and focused). While the commander speaks, voice meters beside the name follow the
 * narration's loudness. The headline then types out in sync with the spoken clip (or
 * at a reading pace without one, instantly under reduced motion), and the Well done / Next
 * mission sections appear when it is complete. Screen readers get the full headline at once.
 */
export class AIAnalysisManager {
  static IS_REMOTE = true;

  /**
   * Sound engine used to narrate the Game Over headline (set by the game). Speech is requested
   * from the server – which invokes a text-to-speech service – only when this is set, supported,
   * unmuted and remote mode is on.
   * @type {{ supported: boolean, muted: boolean, playTransmission?: () => void,
   *   playSpeech: (base64: string, commander: string) =>
   *     Promise<{ delay: number, duration: number }|null> }|null}
   */
  static audio = null;

  /** @type {Promise<{title:string,sections:import("../adapters/AIAnalysisAdapter.js").DebriefSection[],isRemote:boolean,rawResponse?:any}>|null} */
  static _pending = null;
  /** @type {{title:string,sections:import("../adapters/AIAnalysisAdapter.js").DebriefSection[],isRemote:boolean,rawResponse?:any}|null} */
  static _cache = null;
  /** Headline reveal in progress (cancelled by the next render). */
  static _reveal = null;
  /** Voice meter animation in progress (stopped by the next render). */
  static _meter = null;

  /**
   * Create an AIAnalysisAdapter with appropriate remote configuration.
   * Async because the signing client is a lazily loaded chunk (see adapters/SignedApi.js).
   * @returns {Promise<AIAnalysisAdapter>}
   */
  static async _createAdapter() {
    const g = /** @type {any} */ (typeof globalThis !== "undefined" ? globalThis : {});
    const proc = g.process;
    const isTestEnv = !!(proc && proc.env && (proc.env.NODE_ENV === "test" || proc.env.VITEST));

    let endpoint = "";
    /** @type {RemoteStorageAdapter|undefined} */
    let remoteAdapter = undefined;

    try {
      if (!isTestEnv && AIAnalysisManager.IS_REMOTE) {
        endpoint = getApiEndpoint();
        remoteAdapter = new RemoteStorageAdapter({
          fetchFn: await loadSignedFetch(),
          timeoutMs: ANALYSIS_TIMEOUT_MS,
        });
      }
    } catch (_) {
      endpoint = "";
      remoteAdapter = undefined;
    }

    return new AIAnalysisAdapter({
      ...(remoteAdapter ? { remoteAdapter } : {}),
      ...(endpoint ? { endpoint } : {}),
    });
  }

  /**
   * Speech request for this debrief (the commander's voice), or null when sound is
   * off/unsupported or not remote (so no voice is synthesized).
   * @param {"vega"|"altair"} commander
   * @returns {{ voice: string }|null}
   */
  static _speechRequest(commander) {
    const audio = AIAnalysisManager.audio;
    if (!AIAnalysisManager.IS_REMOTE || !audio || !audio.supported || audio.muted) return null;
    return { voice: CONFIG.AUDIO.COMMANDERS[commander].voice };
  }

  /**
   * Kick off an asynchronous analysis. Returns a promise and caches result.
   * Never rejects; resolves to a default suggestion payload on failure.
   * When the server returns a narration clip it is played immediately and not kept in the result;
   * `speechTiming` then resolves to when the voice starts and how long it lasts (see
   * AudioManager.playSpeech), for the headline reveal.
   * @param {object} stats See AIAnalysisAdapter.analyze JSDoc for shape.
   * @returns {Promise<{title:string,sections:import("../adapters/AIAnalysisAdapter.js").DebriefSection[],isRemote:boolean,rawResponse?:any,speechTiming?:Promise<{delay:number,duration:number}|null>}>}
   */
  static async analyze(stats) {
    if (AIAnalysisManager._pending) return AIAnalysisManager._pending;
    const p = (async () => {
      try {
        const adapter = await AIAnalysisManager._createAdapter();
        const commander = commanderOf(stats && stats.history);
        const speech = AIAnalysisManager._speechRequest(commander);
        const { speech: clip, ...result } = await adapter.analyze(
          speech ? { ...stats, speech } : stats
        );
        AIAnalysisManager._cache = result;
        if (clip && AIAnalysisManager.audio) {
          const audio = AIAnalysisManager.audio;
          const speechTiming = Promise.resolve(audio.playSpeech(clip.audio, commander))
            .then((t) => (t && typeof t === "object" ? t : null))
            .catch(() => null);
          return { ...result, speechTiming };
        }
        return { ...result };
      } finally {
        AIAnalysisManager._pending = null;
      }
    })();
    AIAnalysisManager._pending = p;
    return p;
  }

  /**
   * Hide or show the post-game screen's outcome title (#gameOverTitle, e.g. "Ship Down"), which
   * stays hidden while the transmission is incoming.
   * @param {boolean} hidden
   */
  static _setHeadlineHidden(hidden) {
    const el = typeof document !== "undefined" ? document.getElementById("gameOverTitle") : null;
    if (el) el.hidden = hidden;
  }

  /**
   * Show the incoming transmission's signal as locked for a moment before the debrief replaces
   * it (no pause without a transmission on screen or under reduced motion).
   * @param {HTMLElement} container
   * @returns {Promise<void>}
   */
  static async _lockSignal(container) {
    const transmission = container.querySelector(".transmission");
    if (!transmission || prefersReducedMotion()) return;
    transmission.classList.add("locked");
    await new Promise((resolve) => setTimeout(resolve, SIGNAL_LOCK_MS));
  }

  /**
   * Drive the voice meters beside the commander's name from the narration's loudness until the
   * voice has ended (per its timing), then let them rest. Hidden when the clip never plays.
   * @param {HTMLElement[]} meters
   * @param {Promise<{delay:number,duration:number}|null>} speechTiming
   * @returns {{ stop: () => void }}
   */
  static _startMeter(meters, speechTiming) {
    const audio = AIAnalysisManager.audio;
    const bars = meters.map((m) => Array.from(m.children));
    const raf =
      typeof requestAnimationFrame === "function"
        ? requestAnimationFrame
        : (/** @type {() => void} */ fn) => setTimeout(fn, 16);
    let stopped = false;
    let endAt = Infinity;
    const stop = () => {
      stopped = true;
      for (const bar of bars.flat()) bar.style.removeProperty("--level");
    };
    Promise.resolve(speechTiming).then((t) => {
      if (!t) {
        for (const m of meters) m.hidden = true;
        stop();
        return;
      }
      endAt = performance.now() + (t.delay + t.duration) * 1000 + METER_TAIL_MS;
    });
    const tick = () => {
      if (stopped) return;
      if (performance.now() > endAt) {
        stop();
        return;
      }
      const level = audio && typeof audio.speechLevel === "function" ? audio.speechLevel() : 0;
      for (const side of bars) {
        side.forEach((bar, i) => {
          const jitter = 0.8 + Math.random() * 0.4;
          const value = Math.min(1, Math.max(METER_REST, level * METER_SHAPE[i] * jitter));
          bar.style.setProperty("--level", value.toFixed(2));
        });
      }
      raf(tick);
    };
    raf(tick);
    return { stop };
  }

  /**
   * How to type the headline: in sync with the voice when its timing arrives in time, otherwise
   * at a reading pace; instantly under reduced motion.
   * @param {string} title
   * @param {Promise<{delay:number,duration:number}|null>|undefined} speechTiming
   * @returns {Promise<{ delayMs: number, durationMs: number, instant: boolean }>}
   */
  static async _revealTiming(title, speechTiming) {
    if (prefersReducedMotion()) return { delayMs: 0, durationMs: 0, instant: true };
    const timing = speechTiming
      ? await Promise.race([
          speechTiming,
          new Promise((resolve) => setTimeout(() => resolve(null), SPEECH_TIMING_WAIT_MS)),
        ])
      : null;
    if (timing && timing.duration > 0) {
      return { delayMs: timing.delay * 1000, durationMs: timing.duration * 1000, instant: false };
    }
    const durationMs = Math.min(
      TYPE_MAX_MS,
      Math.max(TYPE_MIN_MS, title.length * TYPE_MS_PER_CHAR)
    );
    return { delayMs: 0, durationMs, instant: false };
  }

  /**
   * Render analysis into the post-game message container.
   * - If a result is cached, renders immediately
   * - Otherwise triggers analyze(stats) and updates when ready
   * @param {HTMLElement|null} container Typically the #postGameMessage element
   * @param {object} stats
   * @param {object|null} runSummary
   * @param {boolean} isRemote Whether remote mode is enabled
   * @param {((result: {title:string,sections:import("../adapters/AIAnalysisAdapter.js").DebriefSection[],isRemote:boolean,rawResponse?:any}) => void)|null} [onComplete] Optional callback when analysis completes
   * @param {import("./RunHistory.js").DebriefHistory|null} [history] Mission number, story
   *   hooks and commander sent with the request (see RunHistory)
   */
  static render(
    container,
    stats,
    runSummary = null,
    isRemote = true,
    onComplete = null,
    history = null
  ) {
    if (!container) return;
    const locale =
      (runSummary && runSummary.device && runSummary.device.locale) ||
      (typeof navigator !== "undefined" ? navigator.language : null);
    const labels = debriefLabels(locale);
    const commander = commanderOf(history);
    const sender = labels[commander];
    container.classList.remove("commander-vega", "commander-altair");
    container.classList.add(`commander-${commander}`);
    if (AIAnalysisManager._reveal) AIAnalysisManager._reveal.cancel();
    AIAnalysisManager._reveal = null;
    if (AIAnalysisManager._meter) AIAnalysisManager._meter.stop();
    AIAnalysisManager._meter = null;

    const clear = () => {
      try {
        while (container.firstChild) container.removeChild(container.firstChild);
      } catch {
        /* non-critical cleanup of container children */
      }
    };

    const doRender = (payload) => {
      clear();
      AIAnalysisManager._setHeadlineHidden(false);
      const title = String(payload?.title ?? "");
      /** @type {HTMLElement|null} */
      let typed = null;
      try {
        const from = document.createElement("p");
        from.className = "debrief-from";
        from.textContent = sender;
        // Voice meters flank the name while the commander speaks (keeps the name centred).
        const audio = AIAnalysisManager.audio;
        if (
          payload?.speechTiming &&
          audio &&
          typeof audio.speechLevel === "function" &&
          !prefersReducedMotion()
        ) {
          const left = barsElement("voice-meter", METER_SHAPE.length);
          const right = barsElement("voice-meter mirrored", METER_SHAPE.length);
          from.prepend(left);
          from.append(right);
          AIAnalysisManager._meter = AIAnalysisManager._startMeter(
            [left, right],
            payload.speechTiming
          );
        }
        const headline = document.createElement("p");
        headline.className = "debrief-headline";
        const spoken = document.createElement("span");
        spoken.className = "sr-only";
        spoken.textContent = title;
        typed = document.createElement("span");
        typed.className = "debrief-typed";
        typed.setAttribute("aria-hidden", "true");
        const shown = document.createElement("span");
        shown.className = "debrief-typed-shown";
        const rest = document.createElement("span");
        rest.className = "debrief-typed-rest";
        rest.textContent = title;
        typed.append(shown, rest);
        headline.append(spoken, typed);
        container.append(from, headline);
      } catch {
        /* non-critical creation of the headline */
      }
      /** @type {HTMLElement|null} */
      let debrief = null;
      try {
        debrief = document.createElement("div");
        debrief.className = "debrief pending";
        const sections = Array.isArray(payload?.sections) ? payload.sections : [];
        for (const section of sections) {
          const block = document.createElement("section");
          block.className = `debrief-section debrief-${section.kind === "done" ? "done" : "next"}`;
          const heading = document.createElement("h3");
          heading.textContent = String(section.heading ?? "");
          const ul = document.createElement("ul");
          for (const text of (Array.isArray(section.items) ? section.items : []).slice(0, 8)) {
            const li = document.createElement("li");
            li.textContent = String(text);
            ul.appendChild(li);
          }
          block.append(heading, ul);
          debrief.appendChild(block);
        }
        container.appendChild(debrief);
      } catch {
        /* non-critical creation of debrief sections */
      }
      try {
        const okBtn = /** @type {HTMLButtonElement|null} */ (document.getElementById("okBtn"));
        if (okBtn) {
          okBtn.disabled = false;
          okBtn.hidden = false;
          okBtn.tabIndex = 0;
          try {
            okBtn.focus();
          } catch {
            /* non-critical focus */
          }
          try {
            const postGame = /** @type {HTMLElement|null} */ (
              document.getElementById("gameOverScreen")
            );
            FocusManager.lock(okBtn, {
              scope: postGame || null,
              allowedSelectors: ["#okBtn"],
              preserveScroll: true,
            });
          } catch {
            /* focus lock optional */
          }
        }
      } catch {
        /* non-critical re-enable ok button */
      }
      const showSections = () => {
        if (debrief) debrief.classList.remove("pending");
        if (typed) typed.classList.remove("typing");
      };
      if (!typed) {
        showSections();
        return;
      }
      const box = typed;
      const shownEl = /** @type {HTMLElement} */ (box.querySelector(".debrief-typed-shown"));
      const restEl = /** @type {HTMLElement} */ (box.querySelector(".debrief-typed-rest"));
      /** Placeholder reveal until the timing is known; cancel() shows everything at once. */
      const pending = {
        cancelled: false,
        cancel: () => {
          pending.cancelled = true;
        },
      };
      AIAnalysisManager._reveal = pending;
      AIAnalysisManager._revealTiming(title, payload?.speechTiming).then((timing) => {
        const reveal = typewrite(shownEl, title, {
          ...timing,
          instant: timing.instant || pending.cancelled,
          restEl,
        });
        box.classList.toggle("typing", !(timing.instant || pending.cancelled));
        AIAnalysisManager._reveal = reveal;
        reveal.done.then(showSections);
      });
    };

    if (isRemote) {
      try {
        const p = document.createElement("p");
        p.className = "transmission";
        p.setAttribute("role", "status");
        const label = document.createElement("span");
        label.className = "transmission-label";
        label.textContent = `📡 ${labels.transmission}`;
        const from = document.createElement("span");
        from.className = "transmission-from";
        from.append(sender, barsElement("transmission-signal", SIGNAL_BARS));
        p.append(label, from);
        container.appendChild(p);
        AIAnalysisManager._setHeadlineHidden(true);
      } catch {
        /* non-critical immediate placeholder render */
      }
      try {
        const audio = AIAnalysisManager.audio;
        if (audio && typeof audio.playTransmission === "function") {
          audio.playTransmission();
        }
      } catch {
        /* sound optional */
      }
      try {
        const okBtn = /** @type {HTMLButtonElement|null} */ (document.getElementById("okBtn"));
        if (okBtn) {
          okBtn.disabled = true;
          okBtn.hidden = true;
          okBtn.tabIndex = -1;
          try {
            okBtn.blur();
          } catch {
            /* non-critical blur */
          }
        }
      } catch {
        /* non-critical hide of the ok button during the transmission */
      }
    }

    const maybe = AIAnalysisManager.analyze({
      stats,
      runSummary,
      ...(history ? { history } : {}),
    });
    if (maybe && typeof maybe.then === "function") {
      maybe.then(async (res) => {
        await AIAnalysisManager._lockSignal(container);
        doRender(res);
        if (onComplete && typeof onComplete === "function") {
          try {
            onComplete(res);
          } catch {
            /* ignore callback errors */
          }
        }
      });
    }
  }

  /** Lightweight probe – defers to LeaderboardManager.detectRemote for now. */
  static async detectRemote() {
    try {
      return await LeaderboardManager.detectRemote();
    } catch {
      return false;
    }
  }
}
